import React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { TextInput } from '../../../shared/ui/TextInput';
import { Select } from '../../../shared/ui/Select';
import { ChipSelect } from '../../../shared/ui/ChipSelect';
import { Button } from '../../../shared/ui/Button';
import type { Catalog } from '../../../entities/catalog';

export interface TopicFormValues {
  title: string;
  parentId: string | null;
  html: string;
  groups: string[];
}

interface TopicEditorProps {
  topic: Catalog | null;                 // null — создание нового узла
  parentOptions: { value: string; label: string }[];
  groupOptions: { value: string; label: string }[];
  editor: (value: string, onChange: (html: string) => void) => React.ReactNode;
  onSave: (values: TopicFormValues) => void;
  onCancel: () => void;
  onDelete?: () => void;
  saving?: boolean;
}

const TopicEditor: React.FC<TopicEditorProps> = ({ topic, parentOptions, groupOptions, editor, onSave, onCancel, onDelete, saving }) => {
  const { register, control, handleSubmit, formState: { errors } } = useForm<TopicFormValues>({
    defaultValues: {
      title: topic?.texts.title ?? '',
      parentId: topic?.texts.parentId ?? null,
      html: topic?.texts.html ?? '',
      groups: topic?.groups ?? [],
    },
  });

  const parentId = useWatch({ control, name: 'parentId' });
  const isSection = !parentId; // раздел — верхний уровень; только у него есть видимость по группам

  const options = [{ value: '', label: '— Раздел (верхний уровень) —' }, ...parentOptions];

  return (
    <form className="kb-form" onSubmit={handleSubmit(onSave)}>
      <div className="kb-form-row">
        <label className="kb-form-label">Название</label>
        <TextInput className="kb-input" placeholder="Название" {...register('title', { required: true })} />
        {errors.title && <span className="kb-form-err">Укажите название</span>}
      </div>

      <div className="kb-form-row">
        <label className="kb-form-label">Родитель</label>
        <Controller
          control={control}
          name="parentId"
          render={({ field }) => (
            <Select
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v || null)}
              options={options}
              placeholder="— Раздел (верхний уровень) —"
            />
          )}
        />
      </div>

      {isSection && (
        <div className="kb-form-row">
          <label className="kb-form-label">Видят группы <span className="kb-form-hint">(пусто = виден всем)</span></label>
          <Controller
            control={control}
            name="groups"
            render={({ field }) => (
              <ChipSelect options={groupOptions} value={field.value ?? []} onChange={field.onChange} />
            )}
          />
        </div>
      )}

      <div className="kb-form-row">
        <label className="kb-form-label">Содержимое</label>
        <Controller
          control={control}
          name="html"
          render={({ field }) => <>{editor(field.value, field.onChange)}</>}
        />
      </div>

      <div className="kb-form-actions">
        <Button type="submit" variant="adm-save" disabled={saving}>Сохранить</Button>
        <Button type="button" variant="adm-cancel" onClick={onCancel}>Отмена</Button>
        {topic && onDelete && (
          <Button type="button" variant="adm-delete" onClick={onDelete} style={{ marginLeft: 'auto' }}>Удалить</Button>
        )}
      </div>
    </form>
  );
};

export { TopicEditor };
